import type { ConversationRealtimeCoordinates } from './realtime-config';

/**
 * The rejection classes the tuple predicate distinguishes, plus acceptance.
 *
 * Named rather than counted so a case set can be checked for completeness
 * against the classes themselves: a count literal goes stale silently, and a
 * table that lost a class would still satisfy it.
 */
export type ConversationRealtimeCaseClass =
  | 'accepted'
  | 'coordinate-absent'
  | 'coordinate-blank'
  | 'api-id-malformed'
  | 'region-malformed'
  | 'environment-key-rejected'
  | 'graphql-url-not-appsync'
  | 'graphql-url-wrong-region'
  | 'realtime-url-wrong-api'
  | 'realtime-url-wrong-region';

export interface ConversationRealtimeCase {
  name: string;
  caseClass: ConversationRealtimeCaseClass;
  coordinates: ConversationRealtimeCoordinates;
  expected: 'accept' | 'reject';
}

const VALID: Required<ConversationRealtimeCoordinates> = {
  apiId: 'fixture123',
  graphqlUrl: 'https://fixture123.appsync-api.us-east-1.amazonaws.com/graphql',
  realtimeUrl: 'wss://fixture123.appsync-realtime-api.us-east-1.amazonaws.com/graphql',
  region: 'us-east-1',
  environmentKey: 'gal-3263',
};

const COORDINATE_KEYS = [
  'apiId',
  'graphqlUrl',
  'realtimeUrl',
  'region',
  'environmentKey',
] as const satisfies readonly (keyof ConversationRealtimeCoordinates)[];

function without(key: keyof ConversationRealtimeCoordinates): ConversationRealtimeCoordinates {
  const coordinates: ConversationRealtimeCoordinates = { ...VALID };
  delete coordinates[key];
  return coordinates;
}

/**
 * One declaration of what the predicate does, consumed by the shared unit and
 * by BOTH client suites.
 *
 * Each client maps every case onto its own prefixed bindings and asserts the
 * verdict declared here. That is what makes cross-surface agreement a real
 * property rather than two suites that happen to agree today — and it needs no
 * cross-app import, which ADR-001 Rule 7 forbids even in tests.
 */
export const CONVERSATION_REALTIME_TUPLE_CASES: readonly ConversationRealtimeCase[] = [
  { name: 'a complete tuple', caseClass: 'accepted', coordinates: VALID, expected: 'accept' },
  ...COORDINATE_KEYS.map(
    (key): ConversationRealtimeCase => ({
      name: `${key} absent`,
      caseClass: 'coordinate-absent',
      coordinates: without(key),
      expected: 'reject',
    }),
  ),
  ...COORDINATE_KEYS.map(
    (key): ConversationRealtimeCase => ({
      name: `${key} blank`,
      caseClass: 'coordinate-blank',
      coordinates: { ...VALID, [key]: '   ' },
      expected: 'reject',
    }),
  ),
  {
    name: 'api id carrying an uppercase letter and a hyphen',
    caseClass: 'api-id-malformed',
    coordinates: { ...VALID, apiId: 'ABC-123' },
    expected: 'reject',
  },
  {
    name: 'region in the wrong shape',
    caseClass: 'region-malformed',
    coordinates: { ...VALID, region: 'US-East-1' },
    expected: 'reject',
  },
  {
    name: 'environment key the shared schema rejects',
    caseClass: 'environment-key-rejected',
    coordinates: { ...VALID, environmentKey: 'GAL_3263!' },
    expected: 'reject',
  },
  {
    name: 'graphql url that is not an appsync endpoint',
    caseClass: 'graphql-url-not-appsync',
    coordinates: { ...VALID, graphqlUrl: 'https://fixture123.example.com/graphql' },
    expected: 'reject',
  },
  {
    name: 'graphql url in a different region than declared',
    caseClass: 'graphql-url-wrong-region',
    coordinates: {
      ...VALID,
      graphqlUrl: 'https://fixture123.appsync-api.eu-west-2.amazonaws.com/graphql',
    },
    expected: 'reject',
  },
  {
    name: 'realtime url naming a different api',
    caseClass: 'realtime-url-wrong-api',
    coordinates: {
      ...VALID,
      realtimeUrl: 'wss://other456.appsync-realtime-api.us-east-1.amazonaws.com/graphql',
    },
    expected: 'reject',
  },
  {
    name: 'realtime url naming a different region',
    caseClass: 'realtime-url-wrong-region',
    coordinates: {
      ...VALID,
      realtimeUrl: 'wss://fixture123.appsync-realtime-api.eu-west-2.amazonaws.com/graphql',
    },
    expected: 'reject',
  },
];

/** Every class the table is required to exercise. */
export const CONVERSATION_REALTIME_CASE_CLASSES = [
  'accepted',
  'coordinate-absent',
  'coordinate-blank',
  'api-id-malformed',
  'region-malformed',
  'environment-key-rejected',
  'graphql-url-not-appsync',
  'graphql-url-wrong-region',
  'realtime-url-wrong-api',
  'realtime-url-wrong-region',
] as const satisfies readonly ConversationRealtimeCaseClass[];
